import React from "react";
import i18n from "i18next";
import { formatAppDate } from "util/formatAppDate";
import styles from "./style.module.scss";
import {
  MCP_PRO_MONTHLY_PRICE_USD,
  MCP_SALES_EMAIL,
  MCP_SERVER_URL,
  MCP_TOOLS,
} from "util/mcpSetup";

const Step = ({ number, children }) => (
  <li className={styles.step}>
    <div className={styles.stepHeader}>
      <span className={styles.stepBadge}>{number}</span>
      <div className={styles.stepBody}>{children}</div>
    </div>
  </li>
);

const UsageRow = ({ label, used, limit }) => {
  const pct = limit > 0 ? Math.min(100, Math.round((100 * used) / limit)) : 100;
  return (
    <div className={styles.usageRow}>
      <div className={styles.usageLabel}>
        <span>{label}</span>
        <span>
          {used.toLocaleString(i18n.language)} / {limit.toLocaleString(i18n.language)}
        </span>
      </div>
      <div className={styles.usageBar}>
        <div className={styles.usageFill} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const ProStatus = ({ t, loading, status }) => {
  if (loading) {
    return <p className={styles.hint}>{t("others.20")}…</p>;
  }
  if (!status || !status.entitled) {
    return (
      <div className={styles.proCard}>
        <h5 className={styles.sectionTitle}>{t("mcp_connector.pro_missing_title")}</h5>
        <p className={styles.hint}>
          {t("mcp_connector.pro_missing_body", {
            price: `$${MCP_PRO_MONTHLY_PRICE_USD.toLocaleString("en-US")}`,
          })}
        </p>
        <a
          className={styles.primaryBtn}
          href={`mailto:${MCP_SALES_EMAIL}?subject=${encodeURIComponent("Pivony MCP Pro")}`}
        >
          {t("mcp_connector.pro_contact")}
        </a>
      </div>
    );
  }
  const { limits = {}, usage = {} } = status;
  return (
    <div className={styles.proCard}>
      <div className={styles.proHeader}>
        <h5 className={styles.sectionTitle}>MCP Pro</h5>
        <span className={styles.proBadge}>
          {status.trial && status.period_end
            ? t("mcp_connector.pro_trial_until", {
                date: formatAppDate(status.period_end, i18n.language),
              })
            : t("mcp_connector.pro_active")}
        </span>
      </div>
      <UsageRow
        label={t("mcp_connector.usage_tools")}
        used={usage.tool || 0}
        limit={limits.tool || 0}
      />
      <UsageRow
        label={t("mcp_connector.usage_advisor")}
        used={usage.advisor || 0}
        limit={limits.advisor || 0}
      />
      {status.resets_at && (
        <p className={styles.hint}>
          {t("mcp_connector.usage_resets", {
            date: formatAppDate(status.resets_at, i18n.language),
          })}
        </p>
      )}
    </div>
  );
};

const Representation = ({ t, onBack, loading, status, urlCopied, onCopyUrl }) => (
  <div className={styles.scroll}>
    <div className={styles.container}>
      <button type="button" className={styles.backLink} onClick={onBack}>
        ← {t("settings.334")}
      </button>
      <h4 className={styles.title}>{t("settings.279")}</h4>
      <p className={styles.subtitle}>{t("mcp_connector.subtitle")}</p>

      <section className={styles.guideSection}>
        <ProStatus t={t} loading={loading} status={status} />
      </section>

      <section className={styles.guideSection}>
        <h5 className={styles.sectionTitle}>{t("mcp_connector.claude_title")}</h5>
        <ol className={styles.steps}>
          <Step number="1">
            <p>{t("mcp_connector.claude_step1")}</p>
          </Step>
          <Step number="2">
            <p>{t("mcp_connector.claude_step2")}</p>
            <div className={styles.codeWrap}>
              <pre className={styles.configPre}>{MCP_SERVER_URL}</pre>
              <button type="button" className={styles.copyBtn} onClick={onCopyUrl}>
                {urlCopied ? t("mcp_connector.copied") : t("mcp_connector.copy")}
              </button>
            </div>
          </Step>
          <Step number="3">
            <p>{t("mcp_connector.claude_step3")}</p>
          </Step>
          <Step number="4">
            <p>{t("mcp_connector.claude_step4")}</p>
          </Step>
        </ol>
        <p className={styles.hint}>{t("mcp_connector.claude_org_hint")}</p>
      </section>

      <section className={styles.guideSection}>
        <h5 className={styles.sectionTitle}>{t("mcp_connector.other_title")}</h5>
        <p className={styles.hint}>{t("mcp_connector.other_body")}</p>
      </section>

      <section className={styles.referenceSection}>
        <h5 className={styles.sectionTitle}>{t("mcp_connector.tools_title")}</h5>
        <div className={styles.refTableWrap}>
          <table className={styles.refTable}>
            <tbody>
              {MCP_TOOLS.map((tool) => (
                <tr key={tool.name}>
                  <td>
                    <code>{tool.name}</code>
                  </td>
                  <td>{t(tool.descKey)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.hint}>{t("mcp_connector.tools_note")}</p>
      </section>
    </div>
  </div>
);

export default Representation;
